import * as React from 'react';
import styled, { StyledComponentClass } from 'styled-components';

import {
  ButtonPrimaryRedSkin,
  ButtonSecondaryRedSkin,
} from '@origin-digital/style-guide/dist/cjs/components/Button/skins';

import { BREAK_POINTS, COLOURS, TEXT } from '../../consts/style';
import { ICallToAction } from '../../models/content.interfaces';
import { Link, LinkProps } from '../Link';

const AnchorButtonPrimary = styled(ButtonPrimaryRedSkin.withComponent(Link as any))`
  display: inline-block;
  max-height: 48px;
  height: auto;
  text-decoration: none;
  padding: 12px 24px;
  font-size: 16px;
  color: #fff;
  font-weight: 600;
` as any;

const AnchorButtonSecondary = styled(ButtonSecondaryRedSkin.withComponent(Link as any))`
  display: inline-block;
  max-height: 48px;
  height: auto;
  text-decoration: none;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  &:hover {
    color: ${COLOURS.GREY.WHITE};
  }
` as any;

const StyledLink: StyledComponentClass<LinkProps, any> = styled(Link)`
  font-size: 14px;
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.71;
  letter-spacing: normal;
  text-align: left;
  text-decoration: none;
  display: inline-block;
  color: ${TEXT.LINK.HOVER};

  @media (min-width: ${BREAK_POINTS.MIN.SM}) and (max-width: ${BREAK_POINTS.MAX.MD}) {
    margin-bottom: 16px;
  }
`;

const RightChevron = styled.span`
  position: relative;
  display: inline-block;
  border-style: solid;
  border-width: 0.12em 0.12em 0 0;
  height: 0.45em;
  top: 0.15em;
  left: 0;
  vertical-align: top;
  width: 0.45em;
  transform: rotate(45deg);
  margin-right: 6px;
  margin-top: 6px;
`;

const Container = styled.div`
  display: inline-flex;
  flex-direction: row;
  justify-content: ${(p: { justifyContent: string }) => p.justifyContent};
  @media (max-width: ${BREAK_POINTS.MAX.SM}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const CallToAction = ({
  trackingCode,
  className,
  ...props
}: ICallToAction & { trackingCode: string; className: string }) => {
  switch (props.ctaType) {
    case 'primary':
      return (
        <AnchorButtonPrimary
          data-id={`${trackingCode}-${props.ctaText}`}
          componentType="button"
          href={props.ctaLink}
          target={props.ctaTarget}
          className={className}
        >
          {props.ctaText}
        </AnchorButtonPrimary>
      );
    case 'secondary':
      return (
        <AnchorButtonSecondary
          data-id={`${trackingCode}-${props.ctaText}`}
          componentType="button"
          href={props.ctaLink}
          target={props.ctaTarget}
          className={className}
        >
          {props.ctaText}
        </AnchorButtonSecondary>
      );
    case 'chevron':
      return (
        <StyledLink
          href={props.ctaLink}
          target={props.ctaTarget}
          data-id={`${trackingCode}-${props.ctaText}`}
          componentType="link"
          className={className}
        >
          <RightChevron />
          {props.ctaText}
        </StyledLink>
      );
    default:
      return null;
  }
};

const StyledCallToAction = styled(CallToAction)`
  @media (max-width: ${BREAK_POINTS.MIN.SM}) {
    ${(p: { gap?: number }) => (p.gap ? `margin-bottom: ${p.gap * 2}px` : '')};
  }
  @media (min-width: ${BREAK_POINTS.MIN.SM}) {
    ${(p: { gap?: number }) => (p.gap ? `margin-right: ${p.gap * 4}px` : '')};
  }
`;

export const CallToActions = ({ ctas, centerAlign = true, trackingCode }) =>
  ctas && ctas.length > 0 ? (
    <Container justifyContent={centerAlign ? 'center' : 'flex-start'}>
      <StyledCallToAction
        {...ctas[0]}
        trackingCode={trackingCode}
        gap={ctas.length > 1 ? 4 : undefined}
      />
      {ctas.length > 1 ? <CallToAction {...ctas[1]} trackingCode={trackingCode} /> : null}
    </Container>
  ) : null;
